/**
 * App-local defer wrapper for @directv-digital/dds.services.analytics-service.
 *
 * webpack resolves the BARE package specifier to this module (exact-match `$`
 * alias in next.config.js, client bundles only — same pattern as the
 * fusion.atoms.icons shim) and jest mirrors it via moduleNameMapper, so all
 * consumer files get the deferred fire* functions with zero import churn.
 * This module reaches the real package through its /dist/index subpath, which
 * the exact-match alias does not intercept.
 *
 * Scope note: because the alias also rewrites bare imports made INSIDE
 * node_modules, this defers analytics fired by @directv-digital components
 * themselves (dtv-button CTA clicks, error-display, impressions, …), not just
 * first-party app call sites. That is intended — those calls are the same
 * tap-frame trackEvent surface.
 *
 * Every event-emitting export is either wrapped in runAnalyticsTask (post-paint
 * FIFO, kill switch = the remote per-env toggle in
 * lib/cache/in-memory-cache/analytics-flags.ts — off means fully synchronous
 * legacy behavior) or, for the WAT-12201 excluded events below, dropped. Pure
 * helpers, constants, enums and types pass through unchanged;
 * getAnalyticsAttrs / getAnalyticsDataAttrs return values used during render
 * and MUST stay synchronous. handleLinkClick also passes through: it defers
 * itself via an internal setTimeout(0), and queueing it would let a pagehide
 * flush start that timer after unload — the event would be lost.
 *
 * Related but different: the fireEvent/DataMappingInterface path (see
 * ReportingService) can also arm a timer inside a queued task, but only when
 * the SSAF global hasn't loaded yet — that 50ms polling already loses events
 * across navigation today, so the queue changes nothing there. handleLinkClick
 * would arm its timer on EVERY call, turning a working path into a lossy one;
 * that is why it alone is excluded.
 *
 * Timing asymmetry (accepted): the wrapped functions run whole at flush time,
 * so ones that read live browser state (fireErrorPageViewEvent reads
 * window.location.href, fireModalPageLoadEvent reads document.title) see the
 * page one frame later than the legacy sync path — a same-task pushState/title
 * change lands in the event. App-side call sites in this PR snapshot their
 * payloads (timestamps etc.) before enqueueing; DDS internals cannot be
 * snapshotted from here without vendoring them.
 *
 * The wrap list is pinned to the package's export surface. Drift is NOT
 * harmless: this module replaces the whole package, so a package export
 * missing here is `undefined` for every consumer — a TypeError where it is
 * called. And the package version floats (~ range, no committed lockfile), so
 * drift can arrive with any install. Do NOT rely on the `resolutions` entry to
 * hold a version: it is not applied on the yarn install path (this repo only
 * enforces resolutions through the `generate:package-lock` script, which runs
 * npm-force-resolutions). Observed directly — with `resolutions: 4.3.93` set,
 * `yarn` installed 4.3.94. The direct dependency (`~4.3.95` at the time of
 * writing) is what decides, and it admits any later 4.3.x, which is how
 * publishing 4.3.93 broke devdeploy/next before the matching wrapper entries
 * landed. Two guards: the export-surface test in
 * dds-analytics-wrapper.spec.ts (local runs) and
 * config/analytics-defer-surface-check.js, which fails `next build`/`next dev`
 * (tsc won't catch it — ignoreBuildErrors — and CI does not run jest).
 *
 * WAT-12201: `view_item_list` and `select_item` are excluded from the DIRECTV.com
 * ecommerce catalog, so this module drops them. It is a filter here, and not
 * only a call-site deletion, because package components we cannot edit (agnostic
 * plan cards, media module, dtv-plan-card, form-builder, the Fusion button) emit
 * them through this same alias. The six dedicated emitters are void no-ops, and
 * fireGa4ItemList / fireDataLayerEvent drop the two events by name (the bare ""
 * reset passes). Every export stays, so the surface check holds. Nothing
 * replaces them (no other event or parameter carries the list or selection
 * data), and link_click is untouched.
 *
 * Coverage is limited to imports that resolve through the client alias. Known
 * bypasses, NOT filtered here: @directv-digital/fusion.utils dist/ReportingService.js
 * pushes to the dataLayer directly (no caller today), and DDS's own
 * directv-design-system/services/analytics.ts pushes directly (WAT-12201 removes
 * its view_item_list / select_item emitters at the source instead). Server bundles
 * and anything that reaches the dataLayer without this package are also outside it.
 */
import {
  fireAddToCartEvent as ddsFireAddToCartEvent,
  fireApiResponseEventGA as ddsFireApiResponseEventGA,
  fireCartSummary as ddsFireCartSummary,
  fireCmsTypePageLoadEvent as ddsFireCmsTypePageLoadEvent,
  fireComponentDisplayedEvent as ddsFireComponentDisplayedEvent,
  fireCtaClickEventGA4 as ddsFireCtaClickEventGA4,
  fireCustomClickMultiSearchGA as ddsFireCustomClickMultiSearchGA,
  fireCustomLinkClickForLabels as ddsFireCustomLinkClickForLabels,
  fireCustomLinkClickGA as ddsFireCustomLinkClickGA,
  fireCustomLinkClickImpressions as ddsFireCustomLinkClickImpressions,
  fireDataLayerEvent as ddsFireDataLayerEvent,
  fireEpsilonEvent as ddsFireEpsilonEvent,
  fireErrorDisplayedGA as ddsFireErrorDisplayedGA,
  fireErrorPageViewEvent as ddsFireErrorPageViewEvent,
  fireFormAction as ddsFireFormAction,
  fireGa4HorizontalPlanCard as ddsFireGa4HorizontalPlanCard,
  fireGa4HorizontalPlanCardTrigger as ddsFireGa4HorizontalPlanCardTrigger,
  fireGa4ItemList as ddsFireGa4ItemList,
  fireGa4ProductImpression as ddsFireGa4ProductImpression,
  fireHeaderLinkClickGA as ddsFireHeaderLinkClickGA,
  fireImpression as ddsFireImpression,
  fireLeadCaptureEventGA as ddsFireLeadCaptureEventGA,
  fireLinkClickEvent as ddsFireLinkClickEvent,
  fireModalOpenCloseEventGA as ddsFireModalOpenCloseEventGA,
  fireModalPageLoadEvent as ddsFireModalPageLoadEvent,
  fireModifiedComponentTypePageLoadEvent as ddsFireModifiedComponentTypePageLoadEvent,
  firePageLoadEvent as ddsFirePageLoadEvent,
  fireQuizEventGA as ddsFireQuizEventGA,
  fireRemoveFromCartEvent as ddsFireRemoveFromCartEvent,
  fireSearchEvent as ddsFireSearchEvent,
  fireSearchImpressions as ddsFireSearchImpressions,
  fireSelectItem as ddsFireSelectItem,
  fireTVPromiseGA as ddsFireTVPromiseGA,
  fireTVPromiseZipSearchCaptureGA as ddsFireTVPromiseZipSearchCaptureGA,
  fireTabHandleChange as ddsFireTabHandleChange,
  fireViewItemList as ddsFireViewItemList,
  fireVideoPlayEvent as ddsFireVideoPlayEvent,
  fireVideoPlayerGA as ddsFireVideoPlayerGA,
  fireWidgetCustomLinkClickGA as ddsFireWidgetCustomLinkClickGA,
  fireZoovuEventGA as ddsFireZoovuEventGA,
  pushLinkClick as ddsPushLinkClick,
  pushLinkClickAttrs as ddsPushLinkClickAttrs,
  sendImpressions as ddsSendImpressions
} from "@directv-digital/dds.services.analytics-service/dist/index";
import { runAnalyticsTask } from "./analytics-defer";

// WAT-12201: events removed from UF by business decision.
const EXCLUDED_GA4_EVENTS = new Set(["view_item_list", "select_item"]);

// Signature-preserving no-op for an emitter that only ever pushes an excluded
// event. Typed off the package function so consumers keep their argument types.
const dropped =
  <TArgs extends unknown[]>(_fire: (...args: TArgs) => void) => (..._args: TArgs): void => {};

// Fire-and-forget by contract: every wrapped fire* is void. The queue defers
// execution, so a return value could never reach the caller anyway. If a
// future upstream fire* returns a value that callers consume, it must NOT
// ride this wrapper — the surface check guards export existence, not return
// types, so that break would be silent.
const deferred =
  <TArgs extends unknown[]>(fire: (...args: TArgs) => void) => (...args: TArgs): void => {
    runAnalyticsTask(() => fire(...args));
  };

// component-events
export const fireAddToCartEvent = deferred(ddsFireAddToCartEvent);
export const fireApiResponseEventGA = deferred(ddsFireApiResponseEventGA);
// WAT-11358. Queued like every other fire*: it pushes the GA4 ecommerce reset
// and the payload in one call, so both land in the same task and no other event
// can interleave between them. buildCartSummaryPayload stays synchronous below —
// the caller consumes its return value.
export const fireCartSummary = deferred(ddsFireCartSummary);
/** @deprecated WAT-12201: no-op, select_item is excluded from Upper Funnel. */
export const fireCtaClickEventGA4 = dropped(ddsFireCtaClickEventGA4);
export const fireCustomClickMultiSearchGA = deferred(
  ddsFireCustomClickMultiSearchGA
);
export const fireCustomLinkClickImpressions = deferred(
  ddsFireCustomLinkClickImpressions
);
export const fireErrorDisplayedGA = deferred(ddsFireErrorDisplayedGA);
export const fireErrorPageViewEvent = deferred(ddsFireErrorPageViewEvent);
/** @deprecated WAT-12201: no-op, view_item_list is excluded from Upper Funnel. */
export const fireGa4HorizontalPlanCard = dropped(ddsFireGa4HorizontalPlanCard);
/** @deprecated WAT-12201: no-op, select_item is excluded from Upper Funnel. */
export const fireGa4HorizontalPlanCardTrigger = dropped(
  ddsFireGa4HorizontalPlanCardTrigger
);
// WAT-12201: the generic emitter. Only the excluded event names are dropped. Every other
// call, including the bare `{ ecommerce }` reset ("") and add_to_cart, rides the queue
// unchanged: the reset carries no event or list data, so it is out of scope.
const deferredFireGa4ItemList = deferred(ddsFireGa4ItemList);
export const fireGa4ItemList: typeof ddsFireGa4ItemList = (eventName, items) => {
  if (EXCLUDED_GA4_EVENTS.has(eventName)) {
    return;
  }
  deferredFireGa4ItemList(eventName, items);
};
/** @deprecated WAT-12201: no-op, view_item_list is excluded from Upper Funnel. */
export const fireGa4ProductImpression = dropped(ddsFireGa4ProductImpression);
export const fireHeaderLinkClickGA = deferred(ddsFireHeaderLinkClickGA);
export const fireLeadCaptureEventGA = deferred(ddsFireLeadCaptureEventGA);
export const fireQuizEventGA = deferred(ddsFireQuizEventGA);
export const fireRemoveFromCartEvent = deferred(ddsFireRemoveFromCartEvent);
export const fireSearchEvent = deferred(ddsFireSearchEvent);
export const fireSearchImpressions = deferred(ddsFireSearchImpressions);
export const fireTVPromiseGA = deferred(ddsFireTVPromiseGA);
export const fireTVPromiseZipSearchCaptureGA = deferred(
  ddsFireTVPromiseZipSearchCaptureGA
);
export const fireTabHandleChange = deferred(ddsFireTabHandleChange);
export const fireVideoPlayEvent = deferred(ddsFireVideoPlayEvent);
export const fireVideoPlayerGA = deferred(ddsFireVideoPlayerGA);
export const fireWidgetCustomLinkClickGA = deferred(
  ddsFireWidgetCustomLinkClickGA
);
export const fireZoovuEventGA = deferred(ddsFireZoovuEventGA);

// core-events
export const fireCmsTypePageLoadEvent = deferred(ddsFireCmsTypePageLoadEvent);
export const fireComponentDisplayedEvent = deferred(
  ddsFireComponentDisplayedEvent
);
export const fireCustomLinkClickForLabels = deferred(
  ddsFireCustomLinkClickForLabels
);
export const fireCustomLinkClickGA = deferred(ddsFireCustomLinkClickGA);
export const fireEpsilonEvent = deferred(ddsFireEpsilonEvent);
export const fireFormAction = deferred(ddsFireFormAction);
export const fireImpression = deferred(ddsFireImpression);
export const fireLinkClickEvent = deferred(ddsFireLinkClickEvent);
export const fireModalOpenCloseEventGA = deferred(ddsFireModalOpenCloseEventGA);
export const fireModalPageLoadEvent = deferred(ddsFireModalPageLoadEvent);
// WAT-12687: dim_value is CMS-authored and can carry stray whitespace; trim it
// and skip anything that is not a non-empty string.
const deferredFireModifiedComponentTypePageLoadEvent = deferred(
  ddsFireModifiedComponentTypePageLoadEvent
);
export const fireModifiedComponentTypePageLoadEvent: typeof ddsFireModifiedComponentTypePageLoadEvent = data => {
  const value = typeof data === "string" ? data.trim() : "";
  if (!value) {
    return;
  }
  deferredFireModifiedComponentTypePageLoadEvent(value);
};
export const firePageLoadEvent = deferred(ddsFirePageLoadEvent);
/** @deprecated WAT-12201: no-op, select_item is excluded from Upper Funnel. */
export const fireSelectItem = dropped(ddsFireSelectItem);
/** @deprecated WAT-12201: no-op, view_item_list is excluded from Upper Funnel. */
export const fireViewItemList = dropped(ddsFireViewItemList);
export const sendImpressions = deferred(ddsSendImpressions);

// ga
// WAT-12201: generic pushes from packages and in-repo helpers are filtered by
// event name; every other event is queued unchanged.
const deferredFireDataLayerEvent = deferred(ddsFireDataLayerEvent);
export const fireDataLayerEvent: typeof ddsFireDataLayerEvent = data => {
  // `event` is the legacy ecommerce shape; `event_name` rides a generic trackEvent.
  if (EXCLUDED_GA4_EVENTS.has(data?.event) || EXCLUDED_GA4_EVENTS.has(data?.event_name)) {
    return;
  }
  deferredFireDataLayerEvent(data);
};

// link-click (4.3.103): explicit link_click pushes for activations GTM cannot
// see (keyboard, no DOM click). Void, so they queue like every other fire*.
export const pushLinkClick = deferred(ddsPushLinkClick);
export const pushLinkClickAttrs = deferred(ddsPushLinkClickAttrs);

// Synchronous passthroughs — helpers, constants, enums (and render-time attr
// builders whose return values are consumed immediately). handleLinkClick is
// deliberately NOT queued: it already defers itself via setTimeout(0), and a
// pagehide flush would arm that timer after unload, losing the event.
// The link-click names after titleCase (4.3.103) are helpers, the page-URL hook
// and press-time DOM writers; the writers must stay sync because GTM reads the
// attributes they stamp within the same click.
export {
  EventActions,
  EventCode,
  LINK_POSITION_BODY,
  ReportingDefaults,
  buildCartSummaryPayload,
  constructFullUrl,
  fixAnalyticsValues,
  getAnalyticsAttrs,
  getAnalyticsDataAttrs,
  handleLinkClick,
  tagCookieData,
  titleCase,
  accessibleNameOf,
  createUsePageUrl,
  getPageUrl,
  isKeyboardActivation,
  isNonNavigatingUrl,
  isPressNamedUrl,
  nameCtaOnPress,
  pressedCtaOf,
  pressedElementOf,
  rememberAccessibleName,
  stampPressedLinkAttrs,
  subscribePageUrl,
  toLinkDestination,
  toLinkName,
  toLinkText,
  toModalDestination,
  toModalId,
  withoutName,
  withoutPressName,
  writeLinkNameOnPress
} from "@directv-digital/dds.services.analytics-service/dist/index";
export type {
  Analytics,
  GoogleAnalytics,
  ReportingDefaultsData
} from "@directv-digital/dds.services.analytics-service/dist/index";
